/**
 * Spoken replies for the Marketing Copilot (PR 7, spec §2.4c).
 *
 * Thin adapter over the production `useStreamingTTS` (ElevenLabs
 * multi-context streaming, gapless playback): it owns the decision of
 * whether the copilot may speak, and no-ops when it may not.
 *
 * Speaking requires ALL of:
 *   - the deployment's `voice` feature,
 *   - the admin's copilot `enable_voice` toggle,
 *   - a configured ElevenLabs persona voice (browser streaming is
 *     ElevenLabs-only; a kokoro/azure persona voice degrades to silence
 *     rather than pretending),
 *   - an ElevenLabs credential resolvable for this user (per-user direct
 *     wins → assigned set → none — settingsStore mirrors that resolution),
 *   - the user's own speaker toggle.
 *
 * The first four are `available` (should the speaker button show at all);
 * the last is `enabled` (is it currently unmuted).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useStreamingTTS } from './useStreamingTTS'
import { useFeature } from './useFeature'
import { useSettingsStore } from '@/stores/settingsStore'
import type { StreamingVoiceSettings } from '@/services/ElevenLabsStreamingService'
import type { CopilotPersona } from '@/types/copilot'

export interface UseCopilotTTSOptions {
  persona: CopilotPersona | null
  /** The user's speaker toggle. */
  enabled: boolean
}

export interface UseCopilotTTSReturn {
  /** Whether spoken output is possible at all (drives the toggle's visibility). */
  available: boolean
  isSpeaking: boolean
  /** Last TTS failure, surfaced to the user; null while healthy. */
  error: string | null
  /** Feed one assistant token. No-op unless speaking. */
  processToken: (token: string) => void
  /** Flush buffered text at end of response. No-op unless speaking. */
  flush: () => void
  /** Cut off the current utterance. Always allowed. */
  interrupt: () => void
  /**
   * Stop speaking AND drop the ElevenLabs connection. For leaving the
   * surface entirely — `interrupt` alone keeps the socket warm for the next
   * response, which is wrong once the panel is closed.
   */
  stop: () => Promise<void>
  /**
   * Prepare for a new response. MUST be called from the send gesture —
   * AudioContext creation needs a user gesture under browser autoplay policy.
   */
  prepare: () => Promise<void>
}

/** Persona tuning → ElevenLabs settings; undefined when nothing is set. */
function toVoiceSettings(
  persona: CopilotPersona | null,
): StreamingVoiceSettings | undefined {
  const voice = persona?.voiceConfig
  if (!voice) return undefined
  const settings: StreamingVoiceSettings = {}
  if (voice.stability !== undefined) settings.stability = voice.stability
  if (voice.similarityBoost !== undefined) settings.similarityBoost = voice.similarityBoost
  if (voice.style !== undefined) settings.style = voice.style
  if (voice.useSpeakerBoost !== undefined) settings.useSpeakerBoost = voice.useSpeakerBoost
  if (voice.speed !== undefined) settings.speed = voice.speed
  return Object.keys(settings).length > 0 ? settings : undefined
}

export function useCopilotTTS({
  persona,
  enabled,
}: UseCopilotTTSOptions): UseCopilotTTSReturn {
  const voiceFeature = useFeature('voice')
  const hasElevenLabsKey = useSettingsStore((s) => s.hasElevenLabsKey)

  const voice = persona?.voiceConfig ?? null
  const voiceId = voice && (voice.provider ?? 'elevenlabs') === 'elevenlabs' ? voice.voiceId : ''

  // A failed session stops trying: a revoked key or a stale voice id would
  // otherwise re-fail on every turn while the speaker button still claims to
  // be on. Toggling the speaker off and back on is the retry.
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    setError(null)
  }, [voiceId, enabled])

  const available = Boolean(
    voiceFeature && persona?.voiceEnabled && voiceId && hasElevenLabsKey,
  )
  const active = available && enabled && !error

  const voiceSettings = useMemo(() => toVoiceSettings(persona), [persona])

  const {
    isSpeaking,
    processToken: ttsProcessToken,
    flush: ttsFlush,
    interrupt: ttsInterrupt,
    disconnect: ttsDisconnect,
    prepareForNewStream,
  } = useStreamingTTS({
    voiceId,
    // Inert when false: no WebSocket, no AudioContext, no ElevenLabs traffic.
    enabled: active,
    voiceSettings,
    onError: (e) => {
      console.error('[Copilot TTS]', e)
      setError(e.message || 'Spoken replies are unavailable.')
    },
  })

  const processToken = useCallback(
    (token: string) => {
      if (!active) return
      ttsProcessToken(token)
    },
    [active, ttsProcessToken],
  )

  const flush = useCallback(() => {
    if (!active) return
    ttsFlush()
  }, [active, ttsFlush])

  const prepare = useCallback(async () => {
    if (!active) return
    await prepareForNewStream()
  }, [active, prepareForNewStream])

  const stop = useCallback(async () => {
    ttsInterrupt()
    await ttsDisconnect()
  }, [ttsInterrupt, ttsDisconnect])

  // Muting (or losing availability) must drop the connection, not just stop
  // the audio: `interrupt` closes the current *context* and deliberately
  // keeps the WebSocket warm, and ElevenLabsStreamingService pings it every
  // 15s — so without this an authenticated third-party socket and a running
  // AudioContext would outlive the user's decision to stop listening.
  const wasActive = useRef(active)
  useEffect(() => {
    if (wasActive.current && !active) {
      void stop()
    }
    wasActive.current = active
  }, [active, stop])

  return {
    available,
    error,
    isSpeaking: active && isSpeaking,
    processToken,
    flush,
    // Not gated: muting mid-utterance must still stop the audio.
    interrupt: ttsInterrupt,
    stop,
    prepare,
  }
}
